import React, { lazy, Suspense } from 'react'
import { INLINES, BLOCKS, MARKS } from '@contentful/rich-text-types'
import useHasMounted from '@toasttab/shared/src/hooks/useHasMounted'
import ImageLink from '@toasttab/shared/src/components/molecules/ImageLink/ImageLink'
import NewsTwoColumnContent from '@toasttab/shared/src/components/molecules/News/NewsTwoColumnContent'
import {
  localizeLink,
  rewritePOSLink
} from '@toasttab/shared/src/utils/linkUtils'
import Link from '@toasttab/shared/src/components/atoms/Link/Link'

const HiddenInfogram = lazy(
  () =>
    import(
      '@toasttab/shared/src/components/molecules/HiddenInfogram/HiddenInfogram'
    )
)
const InfogramEmbed = lazy(
  () =>
    import(
      '@toasttab/shared/src/components/molecules/InfogramEmbed/InfogramEmbed'
    )
)
const ProductCTA = lazy(
  () =>
    import(
      '@toasttab/otl/src/components/PageModules/OTLContentSections/ProductCTA/ProductCTA'
    )
)
const TechTips = lazy(
  () =>
    import(
      '@toasttab/otl/src/components/PageModules/OTLContentSections/TechTips/TechTips'
    )
)
const Typography = lazy(
  () => import('@toasttab/shared/src/components/atoms/Typography/Typography')
)

// Get the correct variation for form disclaimers
export const getFormDisclaimerVariation = (node: any) => {
  if (node?.data?.uri?.includes('/privacy')) {
    return 'caption'
  } else {
    return 'default'
  }
}

export const MicrocopyOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary'
            variation='default'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography allowHtml={false} as='h2' classes='mb-6' variation='h2'>
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormStepStatementOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mb-4 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit underline' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary text-[14px]'
            variation='statement'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormDisclaimerOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-4 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      // to get 100% SEO light house score, we need to do this for EN-US
      // todo: do this for other locale
      const isHere = children?.[0] === 'here'
      const linkChildren = isHere
        ? [
            <span className='sr-only' key='sr'>
              Additional information for California residents available
            </span>,
            [children]
          ]
        : children
      return (
        <a
          className='font-normal underline whitespace-nowrap md:whitespace-normal'
          href={uri}
        >
          {linkChildren}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      // Configure different styling for the Login block and the Privacy Statement block
      const linkNode = node?.content?.find(
        (node: any) => node.nodeType === 'hyperlink'
      )
      const variation = linkNode
        ? getFormDisclaimerVariation(linkNode)
        : 'statement'

      return (
        <Suspense fallback={null}>
          <Typography allowHtml={false} as='p' variation={variation}>
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}

export const FormDisclaimerOptionsRequestDemo = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return (
        <div className='text-[#4e5260] [&>p:first-child]:!text-base [&>p:first-child]:leading-[24px] [&>p:first-child]:!mt-4 [&>p:first-child]:!mb-8 lg:[&>p:first-child]:!mb-0 [&>p:first-child]:!px-5 md:[&>p:first-child]:!px-0 [&>p:first-child]:!tracking-normal'>
          {children}
        </div>
      )
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      // to get 100% SEO light house score, we need to do this for EN-US
      // todo: do this for other locale
      const isHere = children?.[0] === 'here'
      const linkChildren = isHere
        ? [
            <span className='sr-only' key='sr'>
              Additional information for California residents available
            </span>,
            [children]
          ]
        : children
      return (
        <a
          className='font-normal underline whitespace-nowrap md:whitespace-normal text-[inherit]'
          href={uri}
        >
          {linkChildren}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <p className='px-2 text-center text-xs mt-3 tracking-[0.2px] leading-4 font-normal'>
          {children}
        </p>
      )
    }
  }
}

export const FormDisclaimerOptionsInverse = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-4 text-white'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a
          className='font-inherit text-inherit text-white underline'
          href={uri}
        >
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      // Configure different styling for the Login block and the Privacy Statement block
      const linkNode = node?.content?.find(
        (node: any) => node.nodeType === 'hyperlink'
      )
      const variation = linkNode
        ? getFormDisclaimerVariation(linkNode)
        : 'statement'

      return (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-white'
            variation={variation}
          >
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}

export const FormDisclaimerEmailSignupOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-6 text-secondary'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary type-caption'
            variation='statement'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const FormDisclaimerOptionsEmailSignupInverse = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.DOCUMENT]: (node: any, children: any) => {
      return <div className='mt-2 text-white'>{children}</div>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a className='font-inherit text-inherit text-white' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-white type-caption'
            variation='statement'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const InformationCardHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-gray-100 mb-4 last:mb-0'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const TestimonialHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-center md:text-left mb-4 !font-normal w-full'
            variation='h4'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='h4'
            classes='text-center md:text-left mb-4 w-full'
            variation='h4'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='h3'
            classes='text-center md:text-left mb-4 w-full'
            variation='h3'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const TestimonialInlineQuoteHeadingOptions = (textAlign = 'center') => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            variation='h4'
            classes={`text-${textAlign} mb-4 !font-normal`}
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='h3'
            variation='h3'
            classes={`text-${textAlign} mb-4 !font-normal`}
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='h4'
            variation='h4'
            classes={`text-${textAlign} mb-4 !font-normal`}
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
})

export const FootnotesOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      const id = uri.startsWith('id:') && uri.replace('id:', '')
      const isAnchor = uri.startsWith('id:')
      if (isAnchor) {
        return <span id={id}>{children}</span>
      }
      return (
        <a className='underline font-normal' href={uri}>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0][1] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary [&>a]:!type-footnote'
            variation='footnote'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.OL_LIST]: (node: any, children: any) => {
      return (
        <ol className='list-decimal list-outside pl-4 marker:text-secondary marker:!type-footnote'>
          {children}
        </ol>
      )
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='text-secondary !type-footnote mb-3'>{children}</li>
    }
  }
}

export const RequestDemoFootnotesOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <p className='text-secondary [&amp;&gt;a]:!type-footnote type-footnote font-normal mb-4 text-gray-75'>
          {children}
        </p>
      )
    }
  }
}

export const StyledListOptions = (
  listStyle?: String,
  languageContext?: any
) => ({
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return (
        <sub className={`leading-[0] align-super text-[10px] ${listStyle}`}>
          {text}
        </sub>
      )
    },
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold text-default'>{text}</span>
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const url = node.data.uri || ''

      // Handle case where languageContext is not provided
      if (!languageContext) {
        return (
          <a className='font-normal' href={url}>
            {children}
          </a>
        )
      }

      const localizedUrl = localizeLink(
        url,
        languageContext?.language,
        languageContext
      )
      return (
        <a className='font-normal' href={localizedUrl}>
          {children}
        </a>
      )
    }
  }
})

export const BasicCardOptions = (languageContext?: any) => ({
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-gray-100'
            variation='default'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const url = node.data.uri || ''

      // Handle case where languageContext is not provided
      if (!languageContext) {
        return (
          <a className='font-normal' href={url}>
            {children}
          </a>
        )
      }

      const localizedUrl = localizeLink(
        url,
        languageContext?.language,
        languageContext
      )
      return (
        <a className='font-normal' href={localizedUrl}>
          {children}
        </a>
      )
    }
  }
})

export const BenchmarkingFormDescriptionOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ol className=''>{children}</ol>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return (
        <li className='text-secondary text-[14px] mb-1 inline-flex'>
          <svg
            className='inline basis-4 flex-shrink-0 mr-4 mt-1'
            fill='none'
            height='16'
            viewBox='0 0 24 24'
            width='16'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M4.00049 12.7498L9.60049 18.9498L20.0005 5.0498'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='1.5'
              stroke='#ff4c00'
            />
          </svg>
          {children}
        </li>
      )
    }
  }
}

export const BasicListOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-inside pl-4'>{children}</ul>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return <Link to={uri}>{children}</Link>
    }
  }
}

export const OTLTrackBodyOptions = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
      return <>{node.data.target?.content}</>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='pb-4'
            variation='default'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const GeneralBodyOptions = (languageContext?: any) => ({
  renderNode: {
    table: (node: any, children: any) => {
      let rowIndex = 0 // Keep track of row index manually

      // Map through the rows and apply custom class to the first row
      const renderedRows = React.Children.map(children, (child) => {
        const isFirstRow = rowIndex === 0
        const className = isFirstRow ? '!font-bold' : ''

        // Increment the row index after each row
        rowIndex += 1

        // Wrap the row content with a <tr> and apply the class
        return <tr className={className}>{child}</tr>
      })

      return (
        <table
          className='custom-table'
          style={{
            border: '1px solid black',
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <tbody>{renderedRows}</tbody>
        </table>
      )
    },
    'table-row': (node: any, children: any) => {
      return <>{children}</>
    },
    'table-cell': (node: any, children: any) => {
      return (
        <td
          className='custom-table-cell'
          style={{ padding: '5pt', border: '1px solid black' }}
        >
          {React.Children.map(children, (child) => {
            if (child.type === 'p') {
              // Apply inline style to <p> tags to remove margin-top
              return React.cloneElement(child, {
                style: { marginTop: 0 }
              })
            }
            return child
          })}
        </td>
      )
    },
    // 'table-header-cell': (node: any, children: any) => {
    //   return <th className="!font-bold">{children}</th>
    // },
    // bartending-competitions has PICS
    // gas-station-ideas-and-concepts , 4th-of-july-food- has PICS
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <p className='rich-text-asset'>
              <img
                alt={asset.description || asset.title || ''}
                src={url}
                title={asset.title || ''}
              />
            </p>
          )
        }
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry?.__typename
      const wrapperStyles = 'my-6'
      if (entry && entryId && entryType === 'ContentfulInfogramEmbed') {
        return (
          <div className={wrapperStyles}>
            <Suspense fallback={null}>
              <InfogramEmbed {...entry} />
            </Suspense>
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulHiddenInfogram') {
        return (
          <div className={wrapperStyles}>
            <Suspense fallback={null}>
              <HiddenInfogram {...entry} />
            </Suspense>
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulProductCta') {
        return (
          <div className={wrapperStyles}>
            <Suspense fallback={null}>
              <ProductCTA {...entry} />
            </Suspense>
          </div>
        )
      }
      if (entry && entryId && entryType === 'ContentfulTechTips') {
        return (
          <div className={wrapperStyles}>
            <Suspense fallback={null}>
              <TechTips {...entry} />
            </Suspense>
          </div>
        )
      }
    },
    // 10 BEST and Most Profitable Bar Foods and Menu Items - has IG
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry.__typename
      if (entry && entryId && entryType === 'ContentfulInstagramEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: entry.embedCode.embedCode
            }}
          />
        )
      }
      if (entry && entryId && entryType === 'ContentfulMediaWidgetEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: entry.embedCode.embedCode
            }}
          />
        )
      }
      if (entry && entryId && entryType === 'ContentfulImageLink') {
        const { url, title, image } = entry
        const { url: imageUrl, contentType } = image.file
        return (
          <a className='image-link' href={url}>
            <img
              alt={image.description || image.title || ''}
              src={imageUrl}
              title={image.title || ''}
            />
          </a>
        )
      }
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const url = node.data.uri || ''
      const rewrittenUrl = rewritePOSLink(url, languageContext)

      return <a href={rewrittenUrl}>{children}</a>
    }
  }
})

export const RichTextContentOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <p className='rich-text-asset'>
              <img
                alt={asset.description || asset.title || ''}
                src={url}
                title={asset.title || ''}
              />
            </p>
          )
        }
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry?.__typename
      if (entry && entryId && entryType === 'ContentfulMdsInfogramEmbed') {
        return (
          <div>
            <Suspense fallback={null}>
              <InfogramEmbed {...entry} />
            </Suspense>
          </div>
        )
      }
    },
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const hasMounted = useHasMounted()
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry.__typename
      if (entry && entryId && entryType === 'ContentfulMdsIframeEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            dangerouslySetInnerHTML={{
              __html: hasMounted ? entry.embedCode.embedCode : ''
            }}
          />
        )
      }
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside pl-4 py-4'>{children}</ul>
    },
    [BLOCKS.HEADING_1]: (node: any, children: any) => {
      return <h1 className='py-2'>{children}</h1>
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return <h2 className='py-2'>{children}</h2>
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return <h3 className='py-2'>{children}</h3>
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return <h4 className='py-2'>{children}</h4>
    },
    [BLOCKS.HEADING_5]: (node: any, children: any) => {
      return <h5 className='py-2'>{children}</h5>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Suspense fallback={null}>
          <Typography allowHtml={false} as='p' classes='text-secondary py-2'>
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}

export const UtilityBodyOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      return (
        <img
          src={`https://${node.data.target.file.url}`}
          className='pb-2 pt-4 rounded-lg max-h-[486px] rounded-lg'
          alt={node.data.target.title}
        />
      )
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='excluded-external-link-treatment'>
          {children}
        </a>
      )
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside pl-4 py-4'>{children}</ul>
    },
    [BLOCKS.HEADING_1]: (node: any, children: any) => {
      return <h1 className='pt-2'>{children}</h1>
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return <h2 className='pt-4'>{children}</h2>
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return (
        <h3 className='pt-10 !font-normal [&_span]:!font-normal'>{children}</h3>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return <h4 className='pt-2'>{children}</h4>
    },
    [BLOCKS.HEADING_5]: (node: any, children: any) => {
      return <h5 className='pt-1'>{children}</h5>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Typography allowHtml={false} as='p' classes='text-secondary pt-4'>
          {children}
        </Typography>
      )
    }
  }
}

export const UtilityListOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return <sub className={`leading-[0] align-super text-[10px]`}>{text}</sub>
    },
    [MARKS.BOLD]: (text: any) => {
      return (
        <h5 className='font-sansPro text-black pb-2 font-sansPro !font-semibold text-[16px] sm:text-[22px] leaading-[24px] sm:leading-[32px]'>
          {text}
        </h5>
      )
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal'>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Suspense fallback={null}>
          <Typography allowHtml={false} as='p' classes='text-secondary'>
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}

export const KeyTakeawaysCardText = {
  renderMark: {
    [MARKS.SUPERSCRIPT]: (text: any) => {
      return <sub>{text}</sub>
    },
    [MARKS.BOLD]: (text: any) => {
      return <span className='font-semibold'>{text}</span>
    }
  },
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal'>
          {children}
        </a>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary !text-otl-p'
          >
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}

export const ResourceBodyOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <p className='rich-text-asset'>
              <img
                alt={asset.description || asset.title || ''}
                src={url}
                title={asset.title || ''}
              />
            </p>
          )
        }
      }
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const { uri } = node.data
      return (
        <a href={uri} className='font-normal underline'>
          {children}
        </a>
      )
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside ml-6 mb-6'>{children}</ul>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.HR]: () => {
      return <hr className='mb-4' />
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='h3'
            classes='mb-6 [&:not(:first-child)]:mt-8'
            variation='h3'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='text-secondary pb-4'
            variation='default'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const BannerTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return children?.length >= 1 && children[0] !== '' ? (
        <Suspense fallback={null}>
          <Typography
            allowHtml={false}
            as='p'
            classes='font-sansPro tracking-[.2px] text-sm'
          >
            {children}
          </Typography>
        </Suspense>
      ) : (
        <>{children}</>
      )
    }
  }
}

export const NewsContentOptions = {
  renderText: (text: string) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
      const asset = node.data.target
      const entryType = asset?.__typename
      if (entryType && entryType === 'ContentfulAsset') {
        const { url, contentType } = asset.file
        const isImage = contentType.includes('image')
        if (isImage) {
          return (
            <div className='my-6'>
              <img
                alt={asset.description || asset.title || ''}
                className='rounded-lg'
                src={url}
                title={asset.title || ''}
              />
            </div>
          )
        }
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const hasMounted = useHasMounted()
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry?.__typename
      if (entry && entryId && entryType === 'ContentfulMdsInfogramEmbed') {
        return (
          <div className='mt-6 mb-2'>
            <Suspense fallback={null}>
              <InfogramEmbed {...entry} />
            </Suspense>
          </div>
        )
      } else if (entry && entryId && entryType === 'ContentfulMdsIframeEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            className='inline-block w-full my-5'
            dangerouslySetInnerHTML={{
              __html: hasMounted ? entry.embedCode.embedCode : ''
            }}
          />
        )
      } else if (entry && entryId && entryType === 'ContentfulImageWithLink') {
        return (
          <Suspense fallback={null}>
            <ImageLink {...entry} classes='mx-6' />
          </Suspense>
        )
      } else if (entry && entryId && entryType === 'ContentfulMdsFeatureItem') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <Suspense fallback={null}>
            <NewsTwoColumnContent content={entry?.desc} image={entry?.media} />
          </Suspense>
        )
      }
    },
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const hasMounted = useHasMounted()
      const entry = node.data.target
      const entryId = node.data.target?.sys.id
      const entryType = entry?.__typename
      if (entry && entryId && entryType === 'ContentfulMdsInfogramEmbed') {
        return (
          <div className='mt-6 mb-2'>
            <Suspense fallback={null}>
              <InfogramEmbed {...entry} />
            </Suspense>
          </div>
        )
      } else if (entry && entryId && entryType === 'ContentfulMdsIframeEmbed') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <span
            className='inline-block my-5'
            dangerouslySetInnerHTML={{
              __html: hasMounted ? entry.embedCode.embedCode : ''
            }}
          />
        )
      } else if (entry && entryId && entryType === 'ContentfulImageWithLink') {
        return (
          <Suspense fallback={null}>
            <ImageLink {...entry} classes='mx-6' />
          </Suspense>
        )
      } else if (entry && entryId && entryType === 'ContentfulMdsFeatureItem') {
        // We need to use a <span> instead of a <div> because Contentful wraps the embed entry with a <p> tag,
        // which would conflict with the block-level semantics of a <div>.
        return (
          <Suspense fallback={null}>
            <NewsTwoColumnContent content={entry?.desc} image={entry?.media} />
          </Suspense>
        )
      }
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => {
      return <ul className='list-disc list-outside pl-4 py-4'>{children}</ul>
    },
    [BLOCKS.OL_LIST]: (node: any, children: any) => {
      return (
        <ol className='list-decimal list-outside pl-4 py-4 [&_li]:!py-2'>
          {children}
        </ol>
      )
    },
    [BLOCKS.HEADING_1]: (node: any, children: any) => {
      return <h1 className='pt-4 pb-2 !text-gray-125'>{children}</h1>
    },
    [BLOCKS.HEADING_2]: (node: any, children: any) => {
      return <h2 className='pt-4 pb-2 !text-gray-125'>{children}</h2>
    },
    [BLOCKS.HEADING_3]: (node: any, children: any) => {
      return <h3 className='pt-4 pb-2 !text-gray-125'>{children}</h3>
    },
    [BLOCKS.HEADING_4]: (node: any, children: any) => {
      return <h4 className='pt-4 pb-2 !text-gray-125'>{children}</h4>
    },
    [BLOCKS.HEADING_5]: (node: any, children: any) => {
      return <h5 className='pt-4 pb-2 !text-gray-125'>{children}</h5>
    },
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className='[&>p]:inline'>{children}</li>
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <Suspense fallback={null}>
          <Typography allowHtml={false} as='p' classes='text-secondary py-2'>
            {children}
          </Typography>
        </Suspense>
      )
    }
  }
}
